import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { ExternalLink, Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import { z } from 'zod'

import { DataTable } from '@/components/DataTable'
import { EmptyValue } from '@/components/EmptyValue'
import { LocalDate } from '@/components/LocalDate'
import { PageHeader } from '@/components/PageHeader'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import {
  CHAT_LIST_FILTER_LABELS,
  CHAT_LIST_FILTERS,
  type ChatListFilter,
  type ChatTicket,
  customerLabel,
} from '@/lib/support/chat'
import { listChatTickets } from '@/lib/support/chat-server'
import { cn } from '@/lib/utils'
import {
  ChannelBadge,
  IdentityBadge,
  PriorityBadge,
  StatusBadge,
} from './-components/chat/badges'

const searchSchema = z.object({
  filter: z.enum(CHAT_LIST_FILTERS).optional().catch(undefined),
  q: z.string().optional(),
})

export const Route = createFileRoute('/_authenticated/admin/support_/chats')({
  validateSearch: searchSchema,
  component: AdminChatsPage,
})

function TicketLink({
  ticket,
  className,
  children,
}: {
  ticket: ChatTicket
  className?: string
  children: React.ReactNode
}) {
  return (
    <Link
      to="/admin/support/chats/$ticket"
      params={{ ticket: String(ticket.number) }}
      className={cn('hover:underline', className)}
    >
      {children}
    </Link>
  )
}

const columns: ColumnDef<ChatTicket>[] = [
  {
    accessorKey: 'number',
    header: '#',
    cell: ({ row }) => (
      <span className="inline-flex items-center gap-1.5">
        {row.original.unreadTeamCount > 0 ? (
          <>
            <span className="size-2 rounded-full bg-primary" aria-hidden />
            <span className="sr-only">Unread</span>
          </>
        ) : null}
        <TicketLink ticket={row.original} className="font-medium">
          #{row.original.number}
        </TicketLink>
      </span>
    ),
  },
  {
    id: 'customer',
    header: 'Customer',
    cell: ({ row }) => {
      const t = row.original
      return (
        <div className="flex min-w-0 flex-col">
          <span className="inline-flex items-center gap-1.5">
            <TicketLink
              ticket={t}
              className={cn(
                'truncate font-medium',
                t.unreadTeamCount > 0 && 'font-semibold',
              )}
            >
              {customerLabel(t)}
            </TicketLink>
            <IdentityBadge verified={t.identityVerified} />
          </span>
          {t.customerName && t.customerEmail ? (
            <span className="text-muted-foreground truncate text-xs">
              {t.customerEmail}
            </span>
          ) : null}
        </div>
      )
    },
  },
  {
    accessorKey: 'channel',
    header: 'Channel',
    cell: ({ row }) => <ChannelBadge channel={row.original.channel} />,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
  {
    accessorKey: 'priority',
    header: 'Priority',
    cell: ({ row }) => <PriorityBadge priority={row.original.priority} />,
  },
  {
    id: 'tags',
    header: 'Tags',
    cell: ({ row }) =>
      row.original.tags.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {row.original.tags.map((tag) => (
            <Badge key={tag} variant="secondary">
              {tag}
            </Badge>
          ))}
        </div>
      ) : (
        <EmptyValue />
      ),
  },
  {
    accessorKey: 'lastMessageText',
    header: 'Last message',
    cell: ({ row }) => (
      <span className="text-muted-foreground block max-w-72 truncate">
        {row.original.lastMessageText ?? <EmptyValue />}
      </span>
    ),
  },
  {
    accessorKey: 'updatedAt',
    header: 'Updated',
    enableSorting: true,
    cell: ({ row }) => <LocalDate iso={row.original.updatedAt} withTime />,
  },
]

function AdminChatsPage() {
  const { filter = 'unresolved', q = '' } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [draft, setDraft] = useState(q)
  useEffect(() => setDraft(q), [q])

  const { data, isPending, error } = useQuery({
    queryKey: ['admin-chat-tickets', filter, q],
    queryFn: () =>
      listChatTickets({ data: { filter, search: q || undefined } }),
    // No push from PostHog, so the inbox polls while it is open.
    refetchInterval: 30_000,
  })

  function setFilter(next: ChatListFilter) {
    void navigate({ search: (prev) => ({ ...prev, filter: next }) })
  }
  function submitSearch() {
    const next = draft.trim()
    void navigate({ search: (prev) => ({ ...prev, q: next || undefined }) })
  }

  const shown = data?.tickets.length ?? 0
  const total = data?.count ?? 0

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Chats"
        description="Support widget and email conversations from PostHog, answered from here."
        actions={
          data ? (
            <Button asChild variant="outline">
              <a href={data.inboxUrl} target="_blank" rel="noreferrer">
                PostHog inbox
                <ExternalLink data-icon="inline-end" />
              </a>
            </Button>
          ) : null
        }
      />

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Couldn't load the inbox</AlertTitle>
          <AlertDescription>{(error as Error).message}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Inbox</CardTitle>
          <CardDescription>
            {isPending
              ? 'Loading…'
              : total > shown
                ? `Newest ${shown} of ${total} tickets`
                : `${total} ticket${total === 1 ? '' : 's'}`}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={filter}
              onValueChange={(v) => {
                if (v) setFilter(v as ChatListFilter)
              }}
              aria-label="Status filter"
            >
              {CHAT_LIST_FILTERS.map((f) => (
                <ToggleGroupItem key={f} value={f}>
                  {CHAT_LIST_FILTER_LABELS[f]}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <form
              className="min-w-64 flex-1 sm:max-w-sm"
              onSubmit={(e) => {
                e.preventDefault()
                submitSearch()
              }}
            >
              <InputGroup>
                <InputGroupInput
                  type="search"
                  placeholder="Name, email, #number or text"
                  aria-label="Search tickets"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                />
                <InputGroupAddon align="inline-end">
                  <InputGroupButton
                    type="submit"
                    size="icon-xs"
                    aria-label="Search"
                  >
                    <Search />
                  </InputGroupButton>
                </InputGroupAddon>
              </InputGroup>
            </form>
          </div>
          <DataTable
            columns={columns}
            data={data?.tickets}
            isPending={isPending}
            emptyMessage={
              q ? 'Nothing matches your search.' : 'No tickets here.'
            }
            pageSize={25}
            initialSorting={[{ id: 'updatedAt', desc: true }]}
          />
        </CardContent>
      </Card>
    </div>
  )
}
